import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, X, Star, TrendingUp, Award } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

const BestCRMMovingCompanyUK = () => {
  const { formatPrice } = useCurrency();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Best CRM for Moving Companies UK: 2026 Comparison & Review",
    "author": {
      "@type": "Organization",
      "name": "Local Business Heroes"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Local Business Heroes",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_localbizheroes/artifacts/792ds0q9_LBH%20Favicon.png"
      }
    },
    "datePublished": "2026-01-15",
    "dateModified": "2026-01-15"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the best CRM for moving companies in the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes is rated #1 by 94 UK moving companies with a 4.9/5 rating. It offers 24/7 emergency call capture, automated quotes, job scheduling, and SMS reminders that reduce no-shows by 85%."
        }
      },
      {
        "@type": "Question",
        "name": "How much does a CRM for moving companies cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Electrician CRM systems range from £150-£1,000/month. Local Business Heroes starts at £297/month with a 14-day trial for just £5, offering excellent value for moving businesses."
        }
      },
      {
        "@type": "Question",
        "name": "Do moving companies really need a CRM?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. UK moving companies using CRM report 40% more emergency callouts, 85% fewer no-shows, and save 15+ hours per week on admin. A CRM pays for itself within the first month."
        }
      }
    ]
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Best CRM for Moving Companies UK" }
  ];

  const crmComparison = [
    {
      name: "Local Business Heroes",
      rating: 4.9,
      users: "94 UK moving companies",
      price: "£297/mo",
      trial: "£5 for 14 days",
      emergencyCallCapture: true,
      automatedQuotes: true,
      smsReminders: true,
      jobScheduling: true,
      reviewManagement: true,
      localSEO: true,
      support: "24/7 UK Support",
      bestFor: "Emergency moving companies & growing businesses",
      pros: ["Emergency call capture 24/7", "Reduces no-shows by 85%", "Automated quote follow-up", "Built for UK moving companies"],
      cons: ["Premium pricing (worth it)"],
      verdict: "Best Overall"
    },
    {
      name: "Jobber",
      rating: 4.5,
      users: "International",
      price: "£25-£140/mo",
      trial: "14 days free",
      emergencyCallCapture: false,
      automatedQuotes: true,
      smsReminders: true,
      jobScheduling: true,
      reviewManagement: false,
      localSEO: false,
      support: "Email only",
      bestFor: "Basic job scheduling",
      pros: ["Affordable", "Simple interface"],
      cons: ["No emergency call capture", "Limited UK features", "No phone support"],
      verdict: "Budget Option"
    },
    {
      name: "ServiceTitan",
      rating: 4.2,
      users: "Enterprise",
      price: "£400+/mo",
      trial: "Contact for demo",
      emergencyCallCapture: true,
      automatedQuotes: true,
      smsReminders: true,
      jobScheduling: true,
      reviewManagement: true,
      localSEO: false,
      support: "Enterprise",
      bestFor: "Large moving companies",
      pros: ["Comprehensive features", "Enterprise-grade"],
      cons: ["Expensive", "Complex setup", "Overkill for small businesses"],
      verdict: "Enterprise"
    }
  ];

  return (
    <>
      <SEO 
        title="Best CRM for Moving Companies UK 2026 | Top 10 Compared ⭐ Rated by Moving Companies"
        description="Rated by 94 UK moving companies. Compare the best CRM systems for moving businesses. Emergency call capture, automated quotes, SMS reminders. Start £5 trial today."
        keywords="best CRM for moving companies UK, electrician CRM software, moving business software UK, emergency electrician software, electrician customer management, job scheduling for moving companies UK"
        url="/best-crm-for-moving companies-uk"
        schemaMarkup={[articleSchema, faqSchema]}
        breadcrumbs={breadcrumbs}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#0E163B] to-[#1a2654] text-white py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Best CRM for Moving Companies UK (2026 Comparison)
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Rated and reviewed by 94 UK moving companies. Compare features, pricing, and real results.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center">
                <Star size={20} className="text-yellow-400 fill-current mr-2" />
                <span>Rated 4.9/5 by moving companies</span>
              </div>
              <div className="flex items-center">
                <TrendingUp size={20} className="text-green-400 mr-2" />
                <span>Updated January 2026</span>
              </div>
            </div>
          </div>
        </section>

        {/* Link to Industry Page */}
        <section className="py-6 px-4 bg-green-50 border-y border-green-200">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-lg font-semibold text-[#0E163B] mb-1">
                  📦 Looking for detailed electrician CRM features?
                </p>
                <p className="text-gray-600">
                  See how Local Business Heroes is specifically built for moving companies
                </p>
              </div>
              <Button 
                asChild
                className="bg-[#FF5A3D] hover:bg-[#E5512E] text-white font-semibold whitespace-nowrap"
                onClick={() => window.scrollTo(0, 0)}
              >
                <Link to="/industries/moving-company">
                  View Full Electrician Solution →
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Quick Summary */}
        <section className="py-12 px-4 bg-white border-b">
          <div className="max-w-6xl mx-auto">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <h2 className="text-2xl font-bold text-[#0E163B] mb-4">🏆 Quick Verdict</h2>
              <p className="text-lg mb-4">
                <strong>Best Overall:</strong> Local Business Heroes - Rated 4.9/5 by 94 UK moving companies. Purpose-built for emergency moving businesses with 24/7 call capture and 85% no-show reduction.
              </p>
              <Button asChild className="bg-[#FF5A3D] hover:bg-[#E5512E]">
                <Link to="/industries/moving-company">Try £5 Trial - Full Access →</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Why Moving Companies Need CRM */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#0E163B] mb-8">
              Why UK Moving Companies Need CRM Software
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-4 text-[#0E163B]">Without CRM</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <X className="text-red-500 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>Miss 40% of emergency calls after hours</span>
                    </li>
                    <li className="flex items-start">
                      <X className="text-red-500 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>30% no-show rate costs £500-£1,000/week</span>
                    </li>
                    <li className="flex items-start">
                      <X className="text-red-500 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>Spend 15+ hours/week on admin and quotes</span>
                    </li>
                    <li className="flex items-start">
                      <X className="text-red-500 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>Lose follow-up leads to competitors</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-500">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-4 text-green-600">With CRM</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>Capture 100% of emergency calls 24/7</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>Reduce no-shows by 85% with automated reminders</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>Save 15+ hours/week on admin tasks</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>Automatic quote follow-up = 40% more bookings</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#0E163B] mb-8">
              Top CRM Systems for Moving Companies Compared
            </h2>
            
            {crmComparison.map((crm, index) => (
              <Card key={index} className={`mb-6 ${index === 0 ? 'border-2 border-[#FF5A3D]' : ''}`}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-[#0E163B]">
                        #{index + 1} {crm.name}
                      </h3>
                      <div className="flex items-center space-x-4 mt-2">
                        <div className="flex items-center">
                          <Star className="text-yellow-400 fill-current mr-1" size={16} />
                          <span className="font-semibold">{crm.rating}/5</span>
                        </div>
                        <span className="text-gray-600">({crm.users})</span>
                        <Award className={`${index === 0 ? 'text-[#FF5A3D]' : 'text-gray-400'}`} size={20} />
                        <span className={`${index === 0 ? 'text-[#FF5A3D] font-semibold' : 'text-gray-600'}`}>
                          {crm.verdict}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[#0E163B]">{crm.price}</div>
                      <div className="text-sm text-gray-600">{crm.trial}</div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4"><strong>Best for:</strong> {crm.bestFor}</p>

                  <div className="grid md:grid-cols-2 gap-6 mb-4">
                    <div>
                      <h4 className="font-semibold text-green-600 mb-2">✓ Pros</h4>
                      <ul className="space-y-1">
                        {crm.pros.map((pro, i) => (
                          <li key={i} className="text-sm text-gray-700">• {pro}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-red-600 mb-2">✗ Cons</h4>
                      <ul className="space-y-1">
                        {crm.cons.map((con, i) => (
                          <li key={i} className="text-sm text-gray-700">• {con}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {index === 0 && (
                    <Button asChild className="w-full bg-[#FF5A3D] hover:bg-[#E5512E]">
                      <Link to="/industries/moving-company">Try {crm.trial} - Full Access →</Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#0E163B] mb-8">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-3">What is the best CRM for moving companies in the UK?</h3>
                  <p className="text-gray-700">
                    Local Business Heroes is rated #1 by 94 UK moving companies with a 4.9/5 rating. It's purpose-built for moving businesses with emergency call capture, automated quotes, and SMS reminders that reduce no-shows by 85%.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-3">How much does a CRM for moving companies cost?</h3>
                  <p className="text-gray-700">
                    Electrician CRM systems range from £150-£1,000/month. Local Business Heroes starts at £297/month with a 14-day trial for just £5, offering excellent ROI for moving businesses. Most moving companies recoup the cost within the first month through reduced no-shows alone.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-3">Do moving companies really need a CRM?</h3>
                  <p className="text-gray-700">
                    Absolutely. UK moving companies using CRM report 40% more emergency callouts, 85% fewer no-shows, and save 15+ hours per week on admin. With no-shows costing £500-£1,000/week, a CRM pays for itself immediately.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Related Comparisons */}
        <section className="py-16 px-4 bg-gray-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#0E163B] mb-8 text-center">
              More CRM Comparison Guides
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-3 text-[#0E163B]">Best CRM for Plumbers UK</h3>
                  <p className="text-gray-600 mb-4">24/7 emergency call capture, instant quotes, and job tracking for plumbing businesses.</p>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/best-crm-for-plumbers-uk">View Comparison →</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-3 text-[#0E163B]">Best CRM for Dentists UK</h3>
                  <p className="text-gray-600 mb-4">Online booking, treatment reminders, and patient retention for dental practices.</p>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/best-crm-for-dentists-uk">View Comparison →</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-3 text-[#0E163B]">Best CRM for Hair Salons UK</h3>
                  <p className="text-gray-600 mb-4">Salon booking, client retention, and automated reminders to reduce no-shows.</p>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/best-crm-for-hair-salons-uk">View Comparison →</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
            <div className="text-center mt-8">
              <Link to="/industries" className="text-[#FF5A3D] hover:underline font-semibold">
                View All Industries →
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Grow Your Electrical Business?
            </h2>
            <p className="text-xl mb-8">
              Join 94 UK moving companies who trust Local Business Heroes. Try it risk-free for 14 days.
            </p>
            <Button asChild size="lg" className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-lg px-10 py-7">
              <Link to="/industries/moving-company">Start £5 Trial - Full Access →</Link>
            </Button>
            <p className="mt-4 text-sm opacity-90">
              ✓ Just £5 to start • ✓ Cancel anytime • ✓ No contracts
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default BestCRMMovingCompanyUK;
